/**
 * Design tokens for the Client Portal UI.
 * Centralizes colors, spacing, typography, and reusable sx objects
 * so pages don't hardcode hex values inline.
 */
import { SxProps, Theme } from '@mui/material/styles';

/* ------------------------------------------------------------------ */
/*  Semantic color tokens                                              */
/* ------------------------------------------------------------------ */

export const colors = {
  // Brand
  brand: '#FAB306',
  brandHover: '#E0A005',
  brandLight: '#FFF8E1',
  brandSubtle: '#FFFBF0',

  // Text
  textPrimary: '#141417',
  textSecondary: '#6F6F6F',
  textTertiary: '#A1A1A1',
  textMuted: '#9CA3AF',

  // Backgrounds
  bgPage: '#FAFAFA',
  bgWhite: '#FFFFFF',
  bgSubtle: '#F9FAFB',
  bgMuted: '#F3F4F6',
  bgCode: '#F9FAFB',

  // Borders
  borderLight: '#EFF0F0',
  borderDefault: '#DBDBDB',
  borderHover: '#D1D5DB',
  borderStrong: '#C7C7C7',

  // Success
  successBg: '#F0FDF4',
  successBgStrong: '#DCFCE7',
  successText: '#166534',
  successIcon: '#16A34A',
  successBorder: '#BBF7D0',

  // Error / Danger
  errorBg: '#FEF2F2',
  errorBgStrong: '#FEE2E2',
  errorText: '#991B1B',
  errorIcon: '#DC2626',
  errorBorder: '#FECACA',

  // Warning
  warningBg: '#FFFBEB',
  warningBgStrong: '#FEF3C7',
  warningText: '#92400E',
  warningIcon: '#D97706',
  warningBorder: '#FDE68A',

  // Approval (WU12) — purple pair for awaiting_approval workflow state.
  // Used by StatusChip + ApprovalCard + WorkflowCard left-border tint.
  approvalIcon: '#7C3AED',
  approvalBg: '#EDE9FE',

  // Info
  infoBg: '#F0F7FF',
  infoBgStrong: '#DBEAFE',
  infoText: '#1E40AF',
  infoIcon: '#3B82F6',
  infoBorder: '#BFDBFE',

  // Metric card backgrounds (pastels)
  metricBlue: '#E1EFFE',
  metricGreen: '#DEF7EC',
  metricPurple: '#F3E8FF',
  metricAmber: '#FEF3C7',
  metricPink: '#FDE8E8',

  // Status
  statusActive: '#16A34A',
  statusInactive: '#6F6F6F',
  statusDraft: '#D97706',
  statusFrozen: '#3B82F6',

  // Chart / Progress
  progressHigh: '#16A34A',
  progressMid: '#FAB306',
  progressLow: '#F57F17',
  progressCritical: '#DC2626',

  // Admin view accents (per Designer A)
  // Slate-900 / Slate-100 from Tailwind's palette — deliberately cool so
  // the admin surface reads as distinct from brand-yellow tenants.
  adminAccent: '#334155',
  adminAccentSubtle: '#F1F5F9',

  // Environment strip colors. `envDev` reuses the existing amber palette;
  // `envProd` is a saturated red that's only ever shown in production.
  envDev: '#FEF3C7',
  envProd: '#DC2626',
} as const;

/* ------------------------------------------------------------------ */
/*  Spacing scale (multiples of 4px via MUI's 8px base)               */
/* ------------------------------------------------------------------ */

export const spacing = {
  /** 4px */  xs: 0.5,
  /** 8px */  sm: 1,
  /** 12px */ md: 1.5,
  /** 16px */ lg: 2,
  /** 20px */ xl: 2.5,
  /** 24px */ xxl: 3,
  /** 32px */ xxxl: 4,
} as const;

/* ------------------------------------------------------------------ */
/*  Typography presets (use with sx={{ ...typo.pageTitle }})           */
/* ------------------------------------------------------------------ */

export const typo = {
  pageTitle: {
    fontSize: 32,
    fontWeight: 800,
    fontFamily: 'Manrope, sans-serif',
    color: colors.textPrimary,
    letterSpacing: '-0.5px',
  },
  pageSubtitle: {
    fontSize: 14,
    fontWeight: 500,
    color: colors.textSecondary,
    lineHeight: 1.5,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 700,
    fontFamily: 'Manrope, sans-serif',
    color: colors.textPrimary,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: colors.textSecondary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  bodyText: {
    fontSize: 14,
    fontWeight: 500,
    color: colors.textPrimary,
  },
  secondaryText: {
    fontSize: 13,
    fontWeight: 500,
    color: colors.textSecondary,
  },
  smallText: {
    fontSize: 12,
    fontWeight: 500,
    color: colors.textSecondary,
  },
  tinyText: {
    fontSize: 11,
    fontWeight: 500,
    color: colors.textTertiary,
  },
  mono: {
    fontFamily: '"Fira Code", "Cascadia Code", "Consolas", monospace',
    fontSize: 13,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.textSecondary,
    mb: 0.75,
  },
  metricValue: {
    fontSize: 32,
    fontWeight: 700,
    fontFamily: 'Manrope, sans-serif',
    color: colors.textPrimary,
  },
  metricLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.textSecondary,
  },
} as const;

/* ------------------------------------------------------------------ */
/*  Reusable sx objects for common patterns                           */
/* ------------------------------------------------------------------ */

/** Standard Paper/Card container */
export const paperSx: SxProps<Theme> = {
  p: 3,
  borderRadius: '16px',
  border: '1px solid rgba(0,0,0,0.06)',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
  mb: 3,
};

/** Centred detail-page content well */
export const detailContainerSx: SxProps<Theme> = {
  maxWidth: 1120,
  mx: 'auto',
  width: '100%',
  px: 3,
};

/** Table header cell */
export const tableHeadCellSx: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: 11,
  fontFamily: 'Manrope, sans-serif',
  color: colors.textSecondary,
  backgroundColor: '#F8F9FA',
  borderBottom: `2px solid ${colors.borderLight}`,
  py: 1.5,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

/** Table body cell */
export const tableCellSx: SxProps<Theme> = {
  fontSize: 13,
  fontFamily: 'Manrope, sans-serif',
  color: colors.textPrimary,
  borderBottom: `1px solid ${colors.borderLight}`,
  py: 1.25,
};

/** Primary action button (yellow brand) */
export const primaryButtonSx: SxProps<Theme> = {
  textTransform: 'none',
  backgroundColor: colors.brand,
  color: colors.textPrimary,
  fontWeight: 700,
  fontFamily: 'Manrope, sans-serif',
  borderRadius: '10px',
  px: 3,
  py: 1,
  fontSize: 14,
  '&:hover': {
    backgroundColor: colors.brandHover,
    boxShadow: '0 4px 12px rgba(250, 179, 6, 0.3)',
    transform: 'translateY(-1px)',
  },
};

/** Secondary/outline button */
export const secondaryButtonSx: SxProps<Theme> = {
  textTransform: 'none',
  borderColor: 'rgba(0,0,0,0.12)',
  color: colors.textPrimary,
  fontWeight: 600,
  fontFamily: 'Manrope, sans-serif',
  borderRadius: '10px',
  px: 2.5,
  py: 0.75,
  fontSize: 13,
  '&:hover': {
    borderColor: colors.brand,
    backgroundColor: 'rgba(250, 179, 6, 0.04)',
    transform: 'translateY(-1px)',
  },
};

/** Standard tab styling */
export const tabsSx: SxProps<Theme> = {
  mb: 3,
  borderBottom: `1px solid ${colors.borderLight}`,
  '& .MuiTab-root': {
    textTransform: 'none',
    fontSize: 15,
    fontWeight: 600,
    fontFamily: 'Manrope, sans-serif',
    color: colors.textSecondary,
    px: 3,
    py: 2,
    minWidth: 'auto',
  },
  '& .Mui-selected': {
    fontWeight: 700,
    color: `${colors.textPrimary} !important`,
  },
  '& .MuiTabs-indicator': {
    backgroundColor: colors.brand,
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
};

/** Search input field */
export const searchFieldSx: SxProps<Theme> = {
  maxWidth: 400,
  '& .MuiInputBase-root': {
    fontSize: 14,
    borderRadius: '12px',
    fontFamily: 'Manrope, sans-serif',
    backgroundColor: '#F8F9FA',
    '&:hover': { backgroundColor: '#F3F4F6' },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0,0,0,0.08)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.brand,
      borderWidth: '2px',
    },
  },
};

/** Hover effect for interactive cards */
export const cardHoverSx: SxProps<Theme> = {
  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
    borderColor: 'rgba(250, 179, 6, 0.4)',
  },
};

/** Page header layout (title + actions) */
export const pageHeaderSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  mb: 4,
  gap: 2,
  flexWrap: 'wrap',
};

/** Dialog action bar */
export const dialogActionsSx: SxProps<Theme> = {
  px: 3,
  py: 2,
  borderTop: `1px solid ${colors.borderLight}`,
};

/** Status chip semantic colors */
export const statusChipColors = {
  active: { bg: colors.successBgStrong, color: colors.successText },
  inactive: { bg: colors.bgMuted, color: colors.textSecondary },
  draft: { bg: colors.warningBgStrong, color: colors.warningText },
  saved: { bg: colors.infoBgStrong, color: colors.infoText },
  error: { bg: colors.errorBgStrong, color: colors.errorText },
  info: { bg: colors.infoBgStrong, color: colors.infoText },
  success: { bg: colors.successBgStrong, color: colors.successText },
  warning: { bg: colors.warningBgStrong, color: colors.warningText },
  published: { bg: colors.successBgStrong, color: colors.successText },

  // Promotion workflow status taxonomy (WU12 §6).
  in_flight: { bg: colors.infoBg, color: colors.infoIcon },
  awaiting_approval: { bg: colors.approvalBg, color: colors.approvalIcon },
  partial: { bg: colors.warningBg, color: colors.warningIcon },
  retrying: { bg: '#F0F7FF', color: colors.infoIcon },
  expired: { bg: colors.bgMuted, color: colors.textTertiary },
  rolled_back: { bg: colors.bgMuted, color: colors.textSecondary },
  unknown: { bg: colors.bgMuted, color: colors.errorBorder },
  // Terminal step/workflow outcomes — previously unmapped, so a FAILED step's
  // chip rendered neutral gray and completed workflows had no green chip.
  completed: { bg: colors.successBgStrong, color: colors.successText },
  failed: { bg: colors.errorBgStrong, color: colors.errorText },
  pending: { bg: colors.bgMuted, color: colors.textSecondary },
  cancelled: { bg: colors.bgMuted, color: colors.textTertiary },
  // Terminal: a newer version was minted on the source env, so this older
  // version's workflow is retired to History. Muted, like cancelled.
  superseded: { bg: colors.bgMuted, color: colors.textTertiary },
  in_progress: { bg: colors.infoBg, color: colors.infoIcon },
  applying: { bg: colors.infoBg, color: colors.infoIcon },
} as const;
